"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import type { AccessibilityStatement } from "../../types/statement";
import { Checkbox } from "@/components/ui/checkbox";
import { TooltipWrapper } from "@/components/ui/tooltip-wrapper";
import { HelpText } from "../help-text";

const TECHNOLOGY_OPTIONS = [
  "HTML",
  "WAI-ARIA",
  "CSS",
  "JavaScript",
  "SMIL",
  "Other",
];

interface TechnicalSpecificationsStepProps {
  data: AccessibilityStatement["technicalSpecs"];
  onChange: (
    data: AccessibilityStatement["technicalSpecs"],
    changedField?: string
  ) => void;
}

export function TechnicalSpecificationsStep({
  data,
  onChange,
}: TechnicalSpecificationsStepProps) {
  // Known Compatibilities
  const addCompatibleBrowser = () => {
    if (data.knownCompatibilities.browsers.length < 5) {
      onChange(
        {
          ...data,
          knownCompatibilities: {
            ...data.knownCompatibilities,
            browsers: [...data.knownCompatibilities.browsers, ""],
          },
        },
        "knownCompatibilities"
      );
    }
  };
  const updateCompatibleBrowser = (index: number, value: string) => {
    const updated = [...data.knownCompatibilities.browsers];
    updated[index] = value;
    onChange(
      {
        ...data,
        knownCompatibilities: {
          ...data.knownCompatibilities,
          browsers: updated,
        },
      },
      "knownCompatibilities"
    );
  };
  const addCompatibleOS = () => {
    if (data.knownCompatibilities.operatingSystems.length < 5) {
      onChange(
        {
          ...data,
          knownCompatibilities: {
            ...data.knownCompatibilities,
            operatingSystems: [
              ...data.knownCompatibilities.operatingSystems,
              "",
            ],
          },
        },
        "knownCompatibilities"
      );
    }
  };
  const updateCompatibleOS = (index: number, value: string) => {
    const updated = [...data.knownCompatibilities.operatingSystems];
    updated[index] = value;
    onChange(
      {
        ...data,
        knownCompatibilities: {
          ...data.knownCompatibilities,
          operatingSystems: updated,
        },
      },
      "knownCompatibilities"
    );
  };
  const addCompatibleAT = () => {
    if (data.knownCompatibilities.assistiveTechnologies.length < 5) {
      onChange(
        {
          ...data,
          knownCompatibilities: {
            ...data.knownCompatibilities,
            assistiveTechnologies: [
              ...data.knownCompatibilities.assistiveTechnologies,
              "",
            ],
          },
        },
        "knownCompatibilities"
      );
    }
  };
  const updateCompatibleAT = (index: number, value: string) => {
    const updated = [...data.knownCompatibilities.assistiveTechnologies];
    updated[index] = value;
    onChange(
      {
        ...data,
        knownCompatibilities: {
          ...data.knownCompatibilities,
          assistiveTechnologies: updated,
        },
      },
      "knownCompatibilities"
    );
  };

  // Known Incompatibilities
  const addIncompatibleBrowser = () => {
    if (data.knownIncompatibilities.browsers.length < 5) {
      onChange(
        {
          ...data,
          knownIncompatibilities: {
            ...data.knownIncompatibilities,
            browsers: [...data.knownIncompatibilities.browsers, ""],
          },
        },
        "knownIncompatibilities"
      );
    }
  };
  const updateIncompatibleBrowser = (index: number, value: string) => {
    const updated = [...data.knownIncompatibilities.browsers];
    updated[index] = value;
    onChange(
      {
        ...data,
        knownIncompatibilities: {
          ...data.knownIncompatibilities,
          browsers: updated,
        },
      },
      "knownIncompatibilities"
    );
  };
  const addIncompatibleOS = () => {
    if (data.knownIncompatibilities.operatingSystems.length < 5) {
      onChange(
        {
          ...data,
          knownIncompatibilities: {
            ...data.knownIncompatibilities,
            operatingSystems: [
              ...data.knownIncompatibilities.operatingSystems,
              "",
            ],
          },
        },
        "knownIncompatibilities"
      );
    }
  };
  const updateIncompatibleOS = (index: number, value: string) => {
    const updated = [...data.knownIncompatibilities.operatingSystems];
    updated[index] = value;
    onChange(
      {
        ...data,
        knownIncompatibilities: {
          ...data.knownIncompatibilities,
          operatingSystems: updated,
        },
      },
      "knownIncompatibilities"
    );
  };
  const addIncompatibleAT = () => {
    if (data.knownIncompatibilities.assistiveTechnologies.length < 5) {
      onChange(
        {
          ...data,
          knownIncompatibilities: {
            ...data.knownIncompatibilities,
            assistiveTechnologies: [
              ...data.knownIncompatibilities.assistiveTechnologies,
              "",
            ],
          },
        },
        "knownIncompatibilities"
      );
    }
  };
  const updateIncompatibleAT = (index: number, value: string) => {
    const updated = [...data.knownIncompatibilities.assistiveTechnologies];
    updated[index] = value;
    onChange(
      {
        ...data,
        knownIncompatibilities: {
          ...data.knownIncompatibilities,
          assistiveTechnologies: updated,
        },
      },
      "knownIncompatibilities"
    );
  };

  // Technology Reliance
  const handleTechToggle = (tech: string, checked: boolean) => {
    const selected = checked
      ? [...(data.technologyReliance?.selected || []), tech]
      : (data.technologyReliance?.selected || []).filter((t) => t !== tech);
    onChange(
      {
        ...data,
        technologyReliance: {
          ...data.technologyReliance,
          selected,
        },
      },
      "technologyReliance"
    );
  };
  const handleTechOtherChange = (value: string) => {
    onChange(
      {
        ...data,
        technologyReliance: {
          ...data.technologyReliance,
          other: value,
        },
      },
      "technologyReliance"
    );
  };

  // Assessment Approach
  const handleAssessmentToggle = (option: string, checked: boolean) => {
    const selected = checked
      ? [...(data.assessmentApproach?.selected || []), option]
      : (data.assessmentApproach?.selected || []).filter((o) => o !== option);
    onChange(
      {
        ...data,
        assessmentApproach: {
          ...data.assessmentApproach,
          selected,
        },
      },
      "assessmentApproach"
    );
  };
  const handleAssessmentOtherChange = (idx: number, value: string) => {
    const updated = [...(data.assessmentApproach?.otherMethods || [])];
    updated[idx] = value;
    onChange(
      {
        ...data,
        assessmentApproach: {
          ...data.assessmentApproach,
          otherMethods: updated,
        },
      },
      "assessmentApproach"
    );
  };
  const addAssessmentOther = () => {
    onChange(
      {
        ...data,
        assessmentApproach: {
          ...data.assessmentApproach,
          otherMethods: [...(data.assessmentApproach?.otherMethods || []), ""],
        },
      },
      "assessmentApproach"
    );
  };

  return (
    <div className="cy:space-y-6">
      <div>
        <h2 className="cy:text-[28px] cy:font-semibold cy:mb-2">
          Technical specifications
        </h2>
      </div>

      <div className="cy:space-y-6">
        {/* Technology Reliance */}
        <div className="cy:mb-6">
          <Label className="cy:text-lg cy:font-semibold cy:mb-1! cy:block">
            Technology reliance{" "}
            <TooltipWrapper className="cy:ml-1" content="Some features rely on specific web technologies to be accessible. Mention these here so users understand why something may not work if a required technology is disabled or not supported." />
          </Label>
          <div className="cy:space-y-2 cy:mb-2">
            {TECHNOLOGY_OPTIONS.map((tech) => (
              <>
                <div
                  key={tech}
                  className="cy:flex cy:items-center cy:gap-2"
                >
                  <Checkbox
                    id={`tech-${tech}`}
                    checked={
                      data.technologyReliance?.selected?.includes(tech) || false
                    }
                    onCheckedChange={(checked) =>
                      handleTechToggle(tech, checked as boolean)
                    }
                  />
                  <Label
                    htmlFor={`tech-${tech}`}
                    className="cy:text-base cy:font-normal cy:mb-0!"
                  >
                    {tech === "Other" ? "Other (Please specify)" : tech}
                  </Label>
                </div>
              </>
            ))}
          </div>
          {data.technologyReliance?.selected?.includes("Other") && (
            <>
              <HelpText className="cy:ml-5 cy:flex cy:w-full">
                Example: Python
              </HelpText>
              <Input
                value={data.technologyReliance?.other || ""}
                onChange={(e) => handleTechOtherChange(e.target.value)}
                className="cy:mt-1"
              />
            </>
          )}
        </div>
        {/* Known Compatibilities */}
        <div>
          <Label className="cy:text-lg cy:font-semibold cy:flex! cy:items-center cy:gap-1 cy:mb-4!">
            Compatibility with user environment
            <TooltipWrapper content="Accessibility may not function consistently across all platforms and assistive tools. WCAG requires features to be accessibility supported. Listing tested environments helps users understand potential compatibility limitations." />
          </Label>

          <div className="cy:space-y-4 cy:px-5 cy:py-4 cy:bg-white cy:rounded-sm">
            {data.knownCompatibilities.browsers.map((browser, index) => (
              <div key={index} className="cy:gap-2 cy:flex cy:flex-col">
                <Label
                  htmlFor={`comp-browser-${index}`}
                  className="cy:text-base cy:font-semibold"
                >
                  Browser {index + 1}
                </Label>
                <HelpText>Example: Safari</HelpText>
                <Input
                  id={`comp-browser-${index}`}
                  value={browser}
                  onChange={(e) =>
                    updateCompatibleBrowser(index, e.target.value)
                  }
                />
              </div>
            ))}
            {data.knownCompatibilities.browsers.length < 5 && (
              <button
                type="button"
                onClick={addCompatibleBrowser}
                className="cy:flex cy:items-center cy:gap-3 cy:bg-transparent cy:border-none cy:cursor-pointer cy:p-0 cy:text-base cy:font-normal cy:text-slate-800 cy:focus:outline-none"
              >
                <span className="cy:flex cy:items-center cy:justify-center cy:w-5 cy:h-5 cy:rounded-full cy:bg-primary cy:text-white cy:text-base">
                  <Plus className="cy:w-3 cy:h-3" />
                </span>
                Add another browser
              </button>
            )}
          </div>

          <div className="cy:space-y-4 cy:px-5 cy:py-4 cy:mt-6 cy:bg-white cy:rounded-sm">
            {data.knownCompatibilities.operatingSystems.map((os, index) => (
              <div key={index} className="cy:gap-2 cy:flex cy:flex-col">
                <Label
                  htmlFor={`comp-os-${index}`}
                  className="cy:text-base cy:font-semibold"
                >
                  Operating system {index + 1}
                </Label>
                <HelpText>Example : Windows 11</HelpText>
                <Input
                  id={`comp-os-${index}`}
                  value={os}
                  onChange={(e) => updateCompatibleOS(index, e.target.value)}
                />
              </div>
            ))}
            {data.knownCompatibilities.operatingSystems.length < 5 && (
              <button
                type="button"
                onClick={addCompatibleOS}
                className="cy:flex cy:items-center cy:gap-3 cy:bg-transparent cy:border-none cy:cursor-pointer cy:p-0 cy:text-base cy:font-normal cy:text-slate-800 cy:focus:outline-none"
              >
                <span className="cy:flex cy:items-center cy:justify-center cy:w-5 cy:h-5 cy:rounded-full cy:bg-primary cy:text-white cy:text-base">
                  <Plus className="cy:w-3 cy:h-3" />
                </span>
                Add operating system
              </button>
            )}
          </div>

          <div className="cy:space-y-4 cy:px-5 cy:py-4 cy:mt-6 cy:bg-white cy:rounded-sm">
            {data.knownCompatibilities.assistiveTechnologies.map(
              (at, index) => (
                <div key={index} className="cy:gap-2 cy:flex cy:flex-col">
                  <Label
                    htmlFor={`comp-at-${index}`}
                    className="cy:text-base cy:font-semibold"
                  >
                    Assistive technologies {index + 1}
                  </Label>
                  <HelpText>Example: JAWS Screen Reader</HelpText>
                  <Input
                    id={`comp-at-${index}`}
                    value={at}
                    onChange={(e) => updateCompatibleAT(index, e.target.value)}
                  />
                </div>
              )
            )}
            {data.knownCompatibilities.assistiveTechnologies.length < 5 && (
              <button
                type="button"
                onClick={addCompatibleAT}
                className="cy:flex cy:items-center cy:gap-3 cy:bg-transparent cy:border-none cy:cursor-pointer cy:p-0 cy:text-base cy:font-normal cy:text-slate-800 cy:focus:outline-none"
              >
                <span className="cy:flex cy:items-center cy:justify-center cy:w-5 cy:h-5 cy:rounded-full cy:bg-primary cy:text-white cy:text-base">
                  <Plus className="cy:w-3 cy:h-3" />
                </span>
                Add another assistive technology
              </button>
            )}
          </div>
        </div>

        {/* Known Incompatibilities */}
        <div className="cy:mb-10">
          <Label className="cy:text-lg cy:font-semibold cy:flex! cy:items-center cy:gap-1 cy:mb-4!">
            Known incompatibility
            <TooltipWrapper content="List unsupported versions of operating systems, browsers, or assistive tools so users can check if their setup works with your site or app." />
          </Label>

          <div className="cy:space-y-4 cy:px-5 cy:py-4 cy:bg-white cy:rounded-sm">
            {data.knownIncompatibilities.browsers.map((browser, index) => (
              <div key={index} className="cy:gap-2 cy:flex cy:flex-col">
                <Label
                  htmlFor={`incomp-browser-${index}`}
                  className="cy:text-base cy:font-semibold"
                >
                  Browser {index + 1}
                </Label>
                <HelpText>Example: Internet Explorer 11</HelpText>
                <Input
                  id={`incomp-browser-${index}`}
                  value={browser}
                  onChange={(e) =>
                    updateIncompatibleBrowser(index, e.target.value)
                  }
                />
              </div>
            ))}
            {data.knownIncompatibilities.browsers.length < 5 && (
              <button
                type="button"
                onClick={addIncompatibleBrowser}
                className="cy:flex cy:items-center cy:gap-3 cy:bg-transparent cy:border-none cy:cursor-pointer cy:p-0 cy:text-base cy:font-normal cy:text-slate-800 cy:focus:outline-none"
              >
                <span className="cy:flex cy:items-center cy:justify-center cy:w-5 cy:h-5 cy:rounded-full cy:bg-primary cy:text-white cy:text-base">
                  <Plus className="cy:w-3 cy:h-3" />
                </span>
                Add another browser
              </button>
            )}
          </div>

          <div className="cy:space-y-4 cy:px-5 cy:py-4 cy:mt-6 cy:bg-white cy:rounded-sm">
            {data.knownIncompatibilities.operatingSystems.map((os, index) => (
              <div key={index} className="cy:gap-2 cy:flex cy:flex-col">
                <Label
                  htmlFor={`incomp-os-${index}`}
                  className="cy:text-base cy:font-semibold"
                >
                  Operating system {index + 1}
                </Label>
                <HelpText>Example: Windows XP</HelpText>
                <Input
                  id={`incomp-os-${index}`}
                  value={os}
                  onChange={(e) => updateIncompatibleOS(index, e.target.value)}
                />
              </div>
            ))}
            {data.knownIncompatibilities.operatingSystems.length < 5 && (
              <button
                type="button"
                onClick={addIncompatibleOS}
                className="cy:flex cy:items-center cy:gap-3 cy:bg-transparent cy:border-none cy:cursor-pointer cy:p-0 cy:text-sm cy:font-normal cy:text-slate-800 cy:focus:outline-none"
              >
                <span className="cy:flex cy:items-center cy:justify-center cy:w-5 cy:h-5 cy:rounded-full cy:bg-primary cy:text-white cy:text-base">
                  <Plus className="cy:w-3 cy:h-3" />
                </span>
                Add operating system
              </button>
            )}
          </div>

          <div className="cy:space-y-4 cy:px-5 cy:py-4 cy:mt-6 cy:bg-white cy:rounded-sm">
            {data.knownIncompatibilities.assistiveTechnologies.map(
              (at, index) => (
                <div key={index} className="cy:gap-2 cy:flex cy:flex-col">
                  <Label
                    htmlFor={`incomp-at-${index}`}
                    className="cy:text-base cy:font-semibold"
                  >
                    Assistive technologies {index + 1}
                  </Label>
                  <HelpText>Example: JAWS Screen Reader</HelpText>
                  <Input
                    id={`incomp-at-${index}`}
                    value={at}
                    onChange={(e) =>
                      updateIncompatibleAT(index, e.target.value)
                    }
                  />
                </div>
              )
            )}
            {data.knownIncompatibilities.assistiveTechnologies.length < 5 && (
              <button
                type="button"
                onClick={addIncompatibleAT}
                className="cy:flex cy:items-center cy:gap-3 cy:bg-transparent cy:border-none cy:cursor-pointer cy:p-0 cy:text-base cy:font-normal cy:text-slate-800 cy:focus:outline-none"
              >
                <span className="cy:flex cy:items-center cy:justify-center cy:w-5 cy:h-5 cy:rounded-full cy:bg-primary cy:text-white cy:text-base">
                  <Plus className="cy:w-3 cy:h-3" />
                </span>
                Add another assistive technology
              </button>
            )}
          </div>
        </div>

        {/* Assessment Approach */}
        <div className="cy:mb-10">
          <Label className="cy:text-lg cy:font-semibold cy:mb-4! cy:flex! cy:gap-1">
            Assessment approach{" "}
            <TooltipWrapper content="Let users know how you checked your website or app for accessibility. This adds transparency to the claims made in your statement." />
          </Label>
          <div className="cy:space-y-2 cy:mb-2">
            <div className="cy:flex cy:items-start cy:gap-2">
              <Checkbox
                id="assessment-self"
                checked={
                  data.assessmentApproach?.selected?.includes(
                    "Self-evaluation"
                  ) || false
                }
                onCheckedChange={(checked) =>
                  handleAssessmentToggle("Self-evaluation", checked as boolean)
                }
              />
              <Label
                htmlFor="assessment-self"
                className="cy:flex cy:flex-col cy:items-start cy:gap-0"
              >
                <span className="cy:text-base cy:font-semibold cy:leading-none">
                  Self-evaluation:
                </span>
                <span className="cy:block cy:text-base cy:text-gray-600  cy:font-normal cy:mt-1">
                  The content was evaluated by your own organization or the
                  developer of the content
                </span>
              </Label>
            </div>
            <div className="cy:flex cy:items-start cy:gap-2">
              <Checkbox
                id="assessment-external"
                checked={
                  data.assessmentApproach?.selected?.includes(
                    "External evaluation"
                  ) || false
                }
                onCheckedChange={(checked) =>
                  handleAssessmentToggle(
                    "External evaluation",
                    checked as boolean
                  )
                }
              />
              <Label
                htmlFor="assessment-external"
                className="cy:flex cy:flex-col cy:items-start cy:gap-0"
              >
                <span className="cy:text-base cy:font-semibold cy:leading-none">
                  External evaluation:
                </span>
                <span className="cy:block cy:text-base cy:text-gray-600 cy:font-normal cy:mt-1">
                  The content was evaluated by an external entity not involved
                  in the design and development process
                </span>
              </Label>
            </div>
            <div className="cy:flex cy:items-start cy:gap-2">
              <Checkbox
                id="assessment-other"
                checked={
                  data.assessmentApproach?.selected?.includes(
                    "Other methods"
                  ) || false
                }
                onCheckedChange={(checked) =>
                  handleAssessmentToggle("Other methods", checked as boolean)
                }
              />
              <Label
                className="cy:text-base cy:font-normal cy:flex cy:flex-col cy:items-baseline cy:gap-0 cy:leading-none"
                htmlFor="assessment-other"
              >
                Other methods
              </Label>
            </div>
          </div>
          {data.assessmentApproach?.selected?.includes("Other methods") && (
            <>
              {(data.assessmentApproach?.otherMethods || [""]).map(
                (method, idx) => (
                  <>
                    {idx === 0 && (
                      <HelpText>Example: User feedback assessment</HelpText>
                    )}
                    <Input
                      key={idx}
                      value={method}
                      onChange={(e) =>
                        handleAssessmentOtherChange(idx, e.target.value)
                      }
                      className="cy:mt-4!"
                    />
                  </>
                )
              )}
              <button
                type="button"
                onClick={addAssessmentOther}
                className="cy:flex cy:items-center cy:gap-2 cy:mt-2 cy:text-base cy:font-normal cy:focus:outline-none"
              >
                <span className="cy:w-5 cy:h-5 cy:bg-blue-600 cy:text-white cy:rounded-full cy:flex cy:items-center cy:justify-center cy:mr-1">
                  <Plus className="cy:w-3 cy:h-3" />
                </span>
                Add another method
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
